import { hijriParts } from '@ly360/i18n';
import { CalendarDays, Check, ChevronLeft, ChevronRight, Clock, Plus, Search, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFormat } from '../display.tsx';

/* ------------------------------------------------------------------------------------------------ popover */

/**
 * A popover anchored to a trigger. Positioned `fixed` from the trigger's rectangle (so scroll containers never clip
 * it) and rendered in place, so it also works inside modal dialogs (which sit in the browser's top layer). It opens
 * below, or above when there is no room, and closes on outside click, Escape or scroll of the page.
 */
/**
 * An ancestor with a transform, filter or backdrop-filter becomes the containing block for `position: fixed`
 * (e.g. the blurred header). Returns its offset so the popover can be placed relative to the viewport anyway.
 */
function fixedOrigin(el: HTMLElement | null): { x: number; y: number } {
  for (let n = el?.parentElement; n && n !== document.body; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (
      cs.transform !== 'none' ||
      cs.filter !== 'none' ||
      (cs.backdropFilter && cs.backdropFilter !== 'none') ||
      cs.perspective !== 'none' ||
      /paint|layout|strict|content/.test(cs.contain)
    ) {
      const r = n.getBoundingClientRect();
      return { x: r.left, y: r.top };
    }
  }
  return { x: 0, y: 0 };
}

function usePopover(open: boolean, setOpen: (o: boolean) => void, minWidth = 0, fixedWidth?: number) {
  const anchor = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  // Off-screen and out of the page flow until placed: an in-flow popover would stretch the anchor it is measured from.
  const hidden: CSSProperties = { position: 'fixed', top: -10000, left: -10000, visibility: 'hidden' };
  const [style, setStyle] = useState<CSSProperties>(hidden);

  const place = useCallback(() => {
    // Measure the control itself (the button or input), not the wrapper that also holds the popover.
    const a = (anchor.current?.firstElementChild ?? anchor.current)?.getBoundingClientRect();
    const p = pop.current;
    if (!a || !p) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(fixedWidth ?? Math.max(a.width, minWidth), vw - 16);
    // Open below the field (the expected place) whenever there is reasonable room, shrinking the list to fit;
    // only flip above when below is cramped and above has more room.
    const below = vh - a.bottom - 12;
    const above = a.top - 12;
    const downward = below >= 200 || below >= above;
    const maxHeight = Math.min(420, downward ? below : above);
    const height = Math.min(p.scrollHeight, maxHeight);
    const top = downward ? a.bottom + 4 : Math.max(8, a.top - 4 - height);
    const rtl = getComputedStyle(anchor.current!).direction === 'rtl';
    let left = rtl ? a.right - width : a.left;
    left = Math.max(8, Math.min(left, vw - width - 8));
    const o = fixedOrigin(p);
    setStyle({
      position: 'fixed',
      top: top - o.y,
      left: left - o.x,
      width,
      maxHeight,
      visibility: 'visible',
    });
  }, [minWidth, fixedWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(hidden);
      return;
    }
    place();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!anchor.current?.contains(t) && !pop.current?.contains(t)) setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return;
      place();
    };
    // Escape closes the popover wherever focus is (and never the dialog around it).
    const onEsc = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      anchor.current?.querySelector<HTMLElement>('button, input')?.focus();
    };
    document.addEventListener('keydown', onEsc, true);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onEsc, true);
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, place, setOpen]);

  return { anchor, pop, style, place };
}

/**
 * A picker may sit inside a <label>. A click in its popover would then also "click" the label, which re-activates
 * the trigger and reopens the list; cancelling the click's default action stops that.
 */
const stopLabel = (e: { preventDefault: () => void }) => e.preventDefault();

/* ------------------------------------------------------------------------------------------------ select */

export interface PickerOption {
  value: string;
  label: string;
  /** Secondary text (e.g. a job title or a phone number), also searched. */
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: PickerOption[];
  /** Shown when the value matches no option (e.g. "None"). */
  placeholder?: string;
  /** Search box in the list (on by default; turn off only where typing makes no sense). */
  searchable?: boolean;
  /** Extra options for what was typed that plain text search misses (e.g. typos: "did you mean …?"). */
  suggest?: (q: string) => PickerOption[];
  /** Offer "Create '<typed text>'" at the end of the list. */
  create?: { label: (q: string) => string; onCreate: (q: string) => void };
  disabled?: boolean;
  required?: boolean;
  'aria-label'?: string;
  'data-testid'?: string;
  className?: string;
  /** Extra classes for the button (e.g. the header's pill style). */
  triggerClassName?: string;
  /** Extra content under the list (e.g. "possible duplicates" warnings). */
  footer?: ReactNode;
}

const fold = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ًͯ-ٰٟـ]/g, '')
    .replace(/[إأآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه');

/** A dropdown that can be searched by typing, with full keyboard support. Replaces native <select> everywhere. */
export function Select({
  id,
  value,
  onChange,
  options,
  placeholder,
  searchable,
  create,
  suggest,
  disabled,
  required,
  className,
  triggerClassName,
  footer,
  ...rest
}: SelectProps) {
  const { t } = useTranslation();
  const auto = useId();
  const listId = `${id ?? auto}-list`;
  const [open, setOpenState] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const button = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const setOpen = useCallback((o: boolean) => {
    setOpenState(o);
    if (!o) setQ('');
  }, []);
  const { anchor, pop, style } = usePopover(open, setOpen, 220);
  // Every dropdown can be searched, so they all behave the same way.
  const canSearch = searchable ?? true;
  const current = options.find((o) => o.value === value);

  const [filtered, suggested] = useMemo(() => {
    const f = fold(q.trim());
    if (!f) return [options, new Set<string>()];
    const direct = options.filter((o) => fold(`${o.label} ${o.hint ?? ''}`).includes(f));
    const have = new Set(direct.map((o) => o.value));
    const extra = (suggest?.(q.trim()) ?? []).filter((o) => !have.has(o.value));
    return [[...direct, ...extra], new Set(extra.map((o) => o.value))];
  }, [options, q, suggest]);
  const showCreate =
    !!create && q.trim().length > 0 && !options.some((o) => fold(o.label) === fold(q.trim()));
  const count = filtered.length + (showCreate ? 1 : 0);

  useEffect(() => {
    if (!open) return;
    const i = Math.max(
      0,
      filtered.findIndex((o) => o.value === value),
    );
    setActive(q ? 0 : i);
    requestAnimationFrame(() => (canSearch ? search.current : list.current)?.focus());
  }, [open]);

  useEffect(() => {
    list.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (i: number) => {
    if (i < filtered.length) {
      const o = filtered[i]!;
      if (o.disabled) return;
      onChange(o.value);
    } else if (showCreate) create!.onCreate(q.trim());
    setOpen(false);
    button.current?.focus();
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return setOpen(true);
      setActive((a) => Math.min(count - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Home' && open) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && open) {
      e.preventDefault();
      setActive(count - 1);
    } else if (e.key === 'Enter' || (e.key === ' ' && !canSearch && open)) {
      e.preventDefault();
      if (!open) setOpen(true);
      else if (count) choose(active);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      button.current?.focus();
    } else if (e.key === 'Tab' && open) setOpen(false);
    else if (
      canSearch &&
      e.key.length === 1 &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey &&
      e.target !== search.current
    ) {
      // Typing on the button starts a search straight away.
      e.preventDefault();
      setQ((x) => x + e.key);
      setActive(0);
      if (!open) setOpen(true);
      requestAnimationFrame(() => search.current?.focus());
    } else if (!canSearch && open && e.key.length === 1) {
      // Typeahead without a search box: jump to the next option starting with the typed letter.
      const k = fold(e.key);
      const start = active + 1;
      const order = [...filtered.slice(start), ...filtered.slice(0, start)];
      const hit = order.find((o) => fold(o.label).startsWith(k));
      if (hit) setActive(filtered.indexOf(hit));
    }
  };

  return (
    <div className={`picker${className ? ` ${className}` : ''}`} ref={anchor}>
      <button
        ref={button}
        id={id}
        type="button"
        className={`select picker-trigger${triggerClassName ? ` ${triggerClassName}` : ''}`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-required={required || undefined}
        aria-label={rest['aria-label']}
        data-testid={rest['data-testid']}
        data-value={value}
        disabled={disabled}
        onClick={() => setOpen(!open)}
        onKeyDown={onKey}
      >
        {current?.icon}
        {/* <bdi> keeps another script's letters in order without flipping the alignment of the field. */}
        <span className={`picker-value${current ? '' : ' is-placeholder'}`}>
          <bdi>{current?.label ?? placeholder ?? ''}</bdi>
        </span>
      </button>
      {open && (
        <div ref={pop} className="picker-pop" style={style} onKeyDown={onKey} onClick={stopLabel}>
          {canSearch && (
            <div className="picker-search">
              <Search size={14} aria-hidden="true" />
              <input
                ref={search}
                value={q}
                dir="auto"
                placeholder={t('common.search')}
                aria-label={t('common.search')}
                aria-controls={listId}
                aria-activedescendant={count ? `${listId}-${active}` : undefined}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActive(0);
                }}
              />
            </div>
          )}
          <ul
            ref={list}
            id={listId}
            role="listbox"
            tabIndex={-1}
            className="picker-list"
            aria-label={rest['aria-label']}
          >
            {filtered.map((o, i) => (
              <li
                key={o.value}
                id={`${listId}-${i}`}
                data-index={i}
                data-value={o.value}
                role="option"
                aria-selected={o.value === value}
                aria-disabled={o.disabled || undefined}
                className={`picker-option${i === active ? ' is-active' : ''}${suggested.has(o.value) ? ' is-suggested' : ''}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(i)}
              >
                {o.icon}
                <span className="picker-label">
                  <bdi>{o.label}</bdi>
                  {suggested.has(o.value) ? (
                    <span className="picker-hint">{t('common.didYouMean')}</span>
                  ) : (
                    o.hint && <span className="picker-hint">{o.hint}</span>
                  )}
                </span>
                {o.value === value && <Check size={14} aria-hidden="true" className="picker-check" />}
              </li>
            ))}
            {showCreate && (
              <li
                id={`${listId}-${filtered.length}`}
                data-index={filtered.length}
                role="option"
                aria-selected={false}
                className={`picker-option picker-create${active === filtered.length ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(filtered.length)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(filtered.length)}
                data-testid="picker-create"
              >
                <Plus size={14} aria-hidden="true" />
                <span className="picker-label">
                  <bdi>{create!.label(q.trim())}</bdi>
                </span>
              </li>
            )}
            {!count && <li className="picker-empty">{t('common.noMatches')}</li>}
          </ul>
          {footer}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------ dates */

const noon = (d: string) => new Date(`${d}T12:00:00Z`);
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (d: string, n: number) => isoDay(new Date(noon(d).getTime() + n * 86_400_000));
const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Reads what people type: 2026-10-14, 14/10/2026, 14-10-2026 or 14.10.2026 (day first, as in the GCC and India). */
export function parseTypedDate(text: string): string | null {
  const s = text.trim().replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));
  let y: number, m: number, d: number;
  const a = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const b = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (a) [y, m, d] = [Number(a[1]), Number(a[2]), Number(a[3])];
  else if (b) [d, m, y] = [Number(b[1]), Number(b[2]), Number(b[3])];
  else return null;
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return isoDay(dt);
}

export interface DateFieldProps {
  id?: string;
  /** "YYYY-MM-DD" or "". */
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * A date field that reads the same in light and dark themes: shows the date in the person's format, accepts typed
 * dates (day first), and opens a full month calendar with Hijri day numbers.
 */
export function DateField({ id, value, onChange, min, max, required, disabled, ...rest }: DateFieldProps) {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const { anchor, pop, style } = usePopover(open, setOpen, 300, 320);
  const display = value && ISO.test(value) ? fmt.date(noon(value)) : '';

  const commit = (raw: string) => {
    setText(null);
    if (!raw.trim()) {
      setInvalid(false);
      return onChange('');
    }
    const parsed = parseTypedDate(raw);
    if (!parsed || (min && parsed < min) || (max && parsed > max)) return setInvalid(true);
    setInvalid(false);
    onChange(parsed);
  };

  return (
    <div className={`picker datefield${invalid ? ' is-invalid' : ''}`} ref={anchor}>
      <input
        id={id}
        className="input"
        dir="ltr"
        inputMode="numeric"
        autoComplete="off"
        placeholder={t('common.datePlaceholder')}
        value={text ?? display}
        required={required}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={rest['aria-label']}
        data-testid={rest['data-testid']}
        data-value={value}
        onFocus={() => setText(value)}
        onChange={(e) => {
          setText(e.target.value);
          const parsed = parseTypedDate(e.target.value);
          if (parsed && (!min || parsed >= min) && (!max || parsed <= max)) {
            setInvalid(false);
            onChange(parsed);
          }
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit((e.target as HTMLInputElement).value);
          }
          if (e.key === 'ArrowDown' && e.altKey) setOpen(true);
        }}
      />
      <div className="picker-adorn">
        {value && !required && !disabled && (
          <button
            type="button"
            className="picker-icon"
            onClick={() => onChange('')}
            aria-label={t('common.clear')}
          >
            <X size={14} />
          </button>
        )}
        <button
          type="button"
          className="picker-icon"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          aria-label={t('common.openCalendar')}
          aria-expanded={open}
          data-testid={rest['data-testid'] ? `${rest['data-testid']}-calendar` : undefined}
        >
          <CalendarDays size={16} />
        </button>
      </div>
      {invalid && <span className="picker-error">{t('common.invalidDate')}</span>}
      {open && (
        <div ref={pop} className="picker-pop" style={style} onClick={stopLabel}>
          <MonthPicker
            value={value}
            min={min}
            max={max}
            lang={i18n.language === 'ar' ? 'ar' : 'en'}
            onPick={(d) => {
              onChange(d);
              setInvalid(false);
              setOpen(false);
            }}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function MonthPicker({
  value,
  min,
  max,
  lang,
  onPick,
  onClose,
}: {
  value: string;
  min?: string;
  max?: string;
  lang: 'en' | 'ar';
  onPick: (d: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const today = isoDay(new Date());
  const [focus, setFocus] = useState(value && ISO.test(value) ? value : today);
  const month = focus.slice(0, 7);
  const grid = useRef<HTMLDivElement>(null);
  const weekStart = lang === 'ar' ? 6 : 0;
  const days = useMemo(() => {
    const first = `${month}-01`;
    const lead = (noon(first).getUTCDay() - weekStart + 7) % 7;
    return Array.from({ length: 42 }, (_, i) => addDays(first, i - lead));
  }, [month, weekStart]);
  const fmtMonth = new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const fmtWd = new Intl.DateTimeFormat(lang, { weekday: 'narrow', timeZone: 'UTC' });
  const fmtLong = new Intl.DateTimeFormat(lang, { dateStyle: 'full', timeZone: 'UTC' });
  const fmtHijri = new Intl.DateTimeFormat(`${lang}-u-ca-islamic-umalqura`, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const allowed = (d: string) => (!min || d >= min) && (!max || d <= max);
  const shiftMonth = (n: number) => {
    const [y, m, d] = focus.split('-').map(Number) as [number, number, number];
    const target = new Date(Date.UTC(y, m - 1 + n, 1, 12));
    const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, last));
    setFocus(isoDay(target));
  };

  useEffect(() => {
    requestAnimationFrame(() => grid.current?.querySelector<HTMLElement>(`[data-date="${focus}"]`)?.focus());
  }, [focus]);

  const onKey = (e: KeyboardEvent) => {
    const rtl = lang === 'ar';
    const map: Record<string, () => void> = {
      ArrowLeft: () => setFocus(addDays(focus, rtl ? 1 : -1)),
      ArrowRight: () => setFocus(addDays(focus, rtl ? -1 : 1)),
      ArrowUp: () => setFocus(addDays(focus, -7)),
      ArrowDown: () => setFocus(addDays(focus, 7)),
      PageUp: () => shiftMonth(-1),
      PageDown: () => shiftMonth(1),
      Home: () => setFocus(today),
      Enter: () => allowed(focus) && onPick(focus),
      ' ': () => allowed(focus) && onPick(focus),
      Escape: () => onClose(),
    };
    const fn = map[e.key];
    if (fn) {
      e.preventDefault();
      e.stopPropagation();
      fn();
    }
  };

  return (
    <div className="monthpicker" onKeyDown={onKey}>
      <div className="mp-head">
        <button
          type="button"
          className="picker-icon"
          onClick={() => shiftMonth(-1)}
          aria-label={t('calendar.month.previous')}
        >
          <ChevronLeft size={16} className="icon-dir" />
        </button>
        <div className="mp-title" aria-live="polite">
          <strong>{fmtMonth.format(noon(`${month}-15`))}</strong>
          <span>{fmtHijri.format(noon(`${month}-15`))}</span>
        </div>
        <button
          type="button"
          className="picker-icon"
          onClick={() => shiftMonth(1)}
          aria-label={t('calendar.month.next')}
        >
          <ChevronRight size={16} className="icon-dir" />
        </button>
      </div>
      <div className="mp-grid" role="grid" ref={grid} aria-label={fmtMonth.format(noon(`${month}-15`))}>
        {days.slice(0, 7).map((d) => (
          <span key={`wd-${d}`} className="mp-wd" role="columnheader">
            {fmtWd.format(noon(d))}
          </span>
        ))}
        {days.map((d) => {
          const other = d.slice(0, 7) !== month;
          return (
            <button
              key={d}
              type="button"
              role="gridcell"
              data-date={d}
              tabIndex={d === focus ? 0 : -1}
              disabled={!allowed(d)}
              aria-selected={d === value}
              aria-label={fmtLong.format(noon(d))}
              className={`mp-day${other ? ' is-other' : ''}${d === today ? ' is-today' : ''}${d === value ? ' is-selected' : ''}`}
              onClick={() => onPick(d)}
            >
              <span>{Number(d.slice(8))}</span>
              <small>{hijriParts(noon(d), 'Asia/Riyadh').day}</small>
            </button>
          );
        })}
      </div>
      <div className="mp-foot">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={!allowed(today)}
          onClick={() => onPick(today)}
        >
          {t('calendar.month.today')}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------ times */

/** A time field (24-hour "HH:MM") with a scrollable list of quarter hours; typing any time also works. */
export function TimeField({
  id,
  value,
  onChange,
  step = 15,
  ...rest
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  step?: number;
  'aria-label'?: string;
  'data-testid'?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const { anchor, pop, style } = usePopover(open, setOpen, 160);
  const list = useRef<HTMLUListElement>(null);
  const times = useMemo(
    () =>
      Array.from(
        { length: (24 * 60) / step },
        (_, i) =>
          `${String(Math.floor((i * step) / 60)).padStart(2, '0')}:${String((i * step) % 60).padStart(2, '0')}`,
      ),
    [step],
  );
  useEffect(() => {
    if (open) list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'center' });
  }, [open]);
  const parse = (s: string) => {
    const m = s.trim().match(/^(\d{1,2})(?::?(\d{2}))?$/);
    if (!m) return null;
    const h = Number(m[1]);
    const mi = Number(m[2] ?? 0);
    return h < 24 && mi < 60 ? `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}` : null;
  };
  return (
    <div className="picker datefield" ref={anchor}>
      <input
        id={id}
        className="input"
        dir="ltr"
        inputMode="numeric"
        autoComplete="off"
        placeholder="09:00"
        value={text ?? value}
        aria-label={rest['aria-label']}
        data-testid={rest['data-testid']}
        onChange={(e) => {
          setText(e.target.value);
          const p = parse(e.target.value);
          if (p) onChange(p);
        }}
        onBlur={() => setText(null)}
      />
      <div className="picker-adorn">
        <button
          type="button"
          className="picker-icon"
          onClick={() => setOpen(!open)}
          aria-label={t('common.chooseTime')}
          aria-expanded={open}
        >
          <Clock size={16} />
        </button>
      </div>
      {open && (
        <div ref={pop} className="picker-pop" style={style} onClick={stopLabel}>
          <ul ref={list} role="listbox" className="picker-list" aria-label={t('common.chooseTime')}>
            {times.map((tm) => (
              <li
                key={tm}
                role="option"
                aria-selected={tm === value}
                className={`picker-option${tm === value ? ' is-active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(tm);
                  setOpen(false);
                }}
              >
                <span className="picker-label" dir="ltr">
                  {tm}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** A date and an optional time, combined into "YYYY-MM-DDTHH:MM" (wall time in the person's zone). */
export function DateTimeField({
  id,
  value,
  onChange,
  ...rest
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
}) {
  const [date, time] = value ? value.split('T') : ['', ''];
  return (
    <div className="datetimefield">
      <DateField
        id={id}
        value={date ?? ''}
        onChange={(d) => onChange(d ? `${d}T${time || '09:00'}` : '')}
        aria-label={rest['aria-label']}
      />
      <TimeField
        id={id ? `${id}-time` : undefined}
        value={time || (date ? '09:00' : '')}
        onChange={(tm) => onChange(`${date || isoDay(new Date())}T${tm}`)}
      />
    </div>
  );
}
